import React, { useEffect, useRef, useState } from "react";
import { mountPluginJsx } from "./pluginJsx";

/**
 * Host genérico de JSX de plugin INLINE — o irmão React do PluginHtmlInline.
 *
 * Monta uma string JSX (corpo de `(React, props) => <JSX/>`) com o motor
 * comum (`pluginJsx.js`): React e Material-UI do app, `props.runtime`, hooks
 * funcionando. Serve a qualquer ponto que precise desenhar "o que o plugin
 * quiser" dentro da sessão: uma action com `render: "jsx"` num slot, o item
 * do menu lateral, uma página inteira do plugin.
 *
 * Duas regras herdadas dos outros hosts:
 *  - re-renderiza SEM remontar quando `props` muda (remontar recria o tipo do
 *    componente e o `useState` do plugin zera a cada tecla);
 *  - falha isolada: JSX quebrado mostra o erro no lugar dele, não derruba a tela.
 */
export default function PluginJsxInline({ jsx, props, style, className, block = false }) {
  const noRef = useRef(null);
  const montadoRef = useRef(null);
  const cacheRef = useRef({});
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let vivo = true;
    if (!jsx || !noRef.current) return undefined;
    setErro(null);
    mountPluginJsx(noRef.current, jsx, props, cacheRef.current)
      .then((montado) => {
        if (!montado) return;
        if (!vivo) { montado.unmount(); return; }
        montadoRef.current = montado;
      })
      .catch((e) => { if (vivo) setErro(String(e?.message || e)); });
    return () => {
      vivo = false;
      if (montadoRef.current) { montadoRef.current.unmount(); montadoRef.current = null; }
    };
    // Só o JSX remonta; props passam pelo render abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsx]);

  useEffect(() => {
    if (montadoRef.current) {
      try { montadoRef.current.render(props); } catch (e) { setErro(String(e?.message || e)); }
    }
  }, [props]);

  if (erro) {
    return <span style={{ color: "#c62828", fontSize: 12 }}>[plugin jsx] {erro}</span>;
  }
  return <div ref={noRef} className={className} style={{ display: block ? "block" : "contents", ...(style || {}) }} />;
}
